import { useTranslation } from "react-i18next"
import { observer } from "mobx-react-lite"
import { useStore } from "../../../contexts/store"
import { MaterialListType, PodClass } from "../../../classes/Pod"

import PodMaterialFile from "./PodMaterialFile"
import PodMaterialFolder from "./PodMaterialFolder"
import { SortableTree } from "../SortableTree/SortableTree"

import { Alert, Box, FormControlLabel, FormGroup, Switch, ToggleButton, ToggleButtonGroup } from "@mui/material"
import UxIcon from "../../Elements/UxIcon"


const PodMaterial = ({pod}: {pod: PodClass}) => {
  const { t } = useTranslation()
  const { uiStore } = useStore()
  const isAdmin = pod.isAllowed('addPdfFile', pod.podId) || pod.isAllowed('editPdfFile', pod.podId)
  const materialList: any[] = pod.getMaterialList(isAdmin)
  const deviceWidth = uiStore.deviceWidth
  const indentationWidth =  uiStore.materialIndentationWidth
  const materialView = uiStore.materialView
  const showEditMaterial = isAdmin ? uiStore.showEditMaterial : false

  const Header = () => (
    <Box sx={{
      alignItems: "center",
      display: "flex",
      flexWrap: "wrap",
      gap: "5px",
      justifyContent: "end",
      padding: "5px 10px 15px 15px"
    }}>
      {deviceWidth !== "tiny" &&
        <UxIcon id="podMaterial" sx={{color: "primary.main", margin: "0 10px"}} fontSize="small" />
      }
      <ToggleButtonGroup
        color="primary"
        value={materialView}
        exclusive
        onChange={(_, newAlignment: string) => {uiStore.setMaterialView(newAlignment ? newAlignment : "list")}}
        aria-label="change between list and tiles view for pod material"
        size="small"
        sx={{
          alignItems:"center",
          display: "flex",
          flexGrow: 1,
          flexWrap: "wrap",
          gap: "5px",
          opacity: showEditMaterial ? 0.4 : 1,
          padding: "10px 0"
        }}
      >
        <ToggleButton sx={{border: "none", fontSize: "18px",textTransform: "unset"}} value={"tiles"}>{t('Material tiles')}</ToggleButton>
        <ToggleButton sx={{border: "none", fontSize: "18px", textTransform: "unset"}} value={"list"}>{t('Material list')}</ToggleButton>
      </ToggleButtonGroup>
      {isAdmin &&
        <FormGroup sx={{backgroundColor: "back.main", border: "2px solid", borderColor: showEditMaterial ? "primary.light" : "lightgray", borderRadius: "10px", padding: "7px 15px"}}>
          <FormControlLabel sx={{color: showEditMaterial ? "primary.dark" : "textcolor.light"}} label={t('Edit material')} control={
            <Switch
              checked={showEditMaterial}
              onChange={() => uiStore.setShowEditMaterial(!showEditMaterial)}
              size="small"
            />
          }/>
        </FormGroup>
      }
    </Box>
  )

  const EmptyPodHelper = () => {
    if(isAdmin) return (
      <Alert severity="warning">
        {t("This pod has no files and folders yet. ")}
        {t("Folders and files can be added at the top right under the 'Edit material' menu item.")}
      </Alert>
    )
    return (
      <Alert severity="info">
       {t('This pod has no files and folders yet. ')}
      </Alert>
    )
  }

  return (
    <Box>
      <Header />
      {showEditMaterial &&
        <Alert severity="info" sx={{padding: "10px 30px"}} >
          {t('helptext_material')}
        </Alert>
      }
      {showEditMaterial ?
        <SortableTree removable indicator indentationWidth={indentationWidth} materialList={materialList} pod={pod} />
      :
        <Box sx={{marginBottom: "15px"}}>
          {materialList.length === 0 &&
            <EmptyPodHelper />
          }
          {materialList.map((item: MaterialListType) => {
            if(item.itemType === "folder" && item.folder) {
              return <PodMaterialFolder key={item.id} folder={item.folder} files={item.children} />
            }
            if(item.itemType === "file" && item.file) {
              return <PodMaterialFile key={item.id} file={item.file} />
            }
            else {
              console.warn("could not render item in materialList", item)
              return null
            }
          })}
        </Box>
      }
    </Box>
  )
}

export default observer(PodMaterial)
